import { useUserPanels } from "dashboard/_root/contexts/UserPanelsContext";
import { useEffect, useState } from "react";
import Button from "_shared/components/Button/Button";
import CustomModal from "_shared/components/Modal/Modal";
import { useModal } from "_shared/contexts/ModalContext";
import { Panel } from "_shared/graphql";
import ConfirmPurchase from "../ConfirmPurchase/ConfirmPurchase";
import RenameTab from "../../../../_shared/components/NavigationTabs/RenameTab";
import styles from "./TabListModal.module.scss";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  panels: Panel[];
}

function TabListModal({ open, onClose, title, subtitle, panels }: Props) {
  const { updatePanels } = useUserPanels();

  const { onErrorMsg } = useModal();

  const [editedPanels, setEditedPanels] = useState(panels);
  const [isSaving, setIsSaving] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [selectedUpgrade, setSelectedUpgrade] = useState<Panel | null>(null);

  useEffect(() => {
    setEditedPanels(panels);
  }, [panels]);

  const saveUpdates = async () => {
    setIsSaving(true);

    const updates = editedPanels.map((panel) => ({
      id: panel.id,
      hidden: panel.hidden,
      name: panel.name,
    }));

    const allHidden = updates.every((update) => update.hidden);
    if (allHidden) {
      onErrorMsg("You must have at least one tab visible.");
    } else {
      await updatePanels(updates);
    }

    setIsSaving(false);
    onClose();
  };

  if (selectedUpgrade) {
    return (
      <ConfirmPurchase
        subId={selectedUpgrade.subscription_id!}
        onClose={() => setSelectedUpgrade(null)}
      />
    );
  }
  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
    >
      <ul className={styles.list}>
        {editedPanels.map((panel) => {
          const isRenaming = panel.id === renamingId;
          return (
            <li className={styles.listItem} key={panel.id}>
              {isRenaming ? (
                <RenameTab
                  originalName={panel.name}
                  onRename={(newName) => {
                    setEditedPanels((prev) =>
                      prev.map((p) => {
                        if (p.id === panel.id) {
                          return { ...p, name: newName };
                        }
                        return p;
                      }),
                    );
                    setRenamingId(null);
                  }}
                  onCancel={() => setRenamingId(null)}
                />
              ) : (
                <>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    defaultChecked={!panel.hidden}
                    id={panel.id}
                    onChange={(e) => {
                      setEditedPanels((prev) =>
                        prev.map((p) => {
                          if (p.id === panel.id) {
                            return { ...p, hidden: !e.target.checked };
                          }
                          return p;
                        }),
                      );
                    }}
                  />
                  {panel.name}
                </>
              )}
              {panel.longbow_panel ? (
                <>
                  {panel.subscription_id && panel.user_subscriptions ? (
                    <div className={styles.purchasedText}>Purchased</div>
                  ) : null}
                  {panel.subscription_id && !panel.user_subscriptions ? (
                    <button
                      onClick={() => setSelectedUpgrade(panel)}
                      className={styles.addButton}
                    >
                      Add
                    </button>
                  ) : null}
                </>
              ) : isRenaming ? null : (
                <button
                  className={styles.editButton}
                  onClick={() => setRenamingId(panel.id)}
                >
                  <i className="fas fa-pen" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <div className={styles.footer}>
        <Button onClick={onClose} variant="secondary">
          Close
        </Button>
        <Button onClick={saveUpdates} loading={isSaving}>
          Save
        </Button>
      </div>
    </CustomModal>
  );
}

export default TabListModal;
